import Checkbox from "@ui-kit/form/Checkbox/Checkbox.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import Icon from "@ui-kit/icons/Icon.web";
import CardElement from "@shared/modules/payments/components/CardElement/CardElement.web";
import NameElement from "@shared/modules/payments/components/auxiliary/NameElement/NameElement.web";
import { useCallback, useEffect, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import PublicCheckoutBillingAddressForm from "@app-contexts/public/shared/checkout/components/steps/review/delivery-methods/PublicCheckoutBillingAddressForm/PublicCheckoutBillingAddressForm.web";
import { UserBasedUserPaymentMethodSerializer } from "@app-contexts/user/features/profile/data/types/payment-types";
import { getCardBrandByName } from "@shared/modules/payments/common/CardBrandLogo/utils/getCardBrand";
import CardBrandLogo from "@shared/modules/payments/common/CardBrandLogo/CardBrandLogo.web";
import ErrorBoundary from "@shared/components/application/other/ErrorBoundary/ErrorBoundary.web";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";

import PaymentTypeAccordion from "../PaymentTypeAccordion.web";

type BaseCardPaymentTypeProps = {
  isDisabled: boolean;
  value?: string;
};

type CardPaymentTypeManualCardEntryProps = BaseCardPaymentTypeProps & {
  manualCardEntryForm: UseFormReturn;
  addressForm: UseFormReturn;
  userHasSavedCards: boolean;
  /**
   * Whether saving manually entered credit cards is supported
   */
  supportsSavingCards: boolean;
  onBillingAddressChange?: (billingAddress: {
    info_home_address_country: string;
  }) => void;
  shouldSaveCard: boolean;
  onToggleSaveCard: (shouldSaveCard: boolean) => void;
  onValidationChange: (isValid: boolean) => void;
  onSelect: () => void;
};

export function CardPaymentTypeManualCardEntry({
  onSelect,
  onValidationChange,
  isDisabled = false,
  value,
  manualCardEntryForm,
  addressForm,
  userHasSavedCards,
  supportsSavingCards,
  onBillingAddressChange,
  shouldSaveCard,
  onToggleSaveCard,
}: CardPaymentTypeManualCardEntryProps) {
  const { t } = useTranslation("checkout");

  const [isNameValid, setIsNameValid] = useState(false);
  const [isCardInputValid, setIsCardInputValid] = useState(false);
  const [isAddressValid, setIsAddressValid] = useState(false);

  useEffect(() => {
    onValidationChange(isNameValid && isCardInputValid && isAddressValid);
  }, [isNameValid, isCardInputValid, isAddressValid]);

  return (
    <PaymentTypeAccordion.Item disabled={isDisabled} value={value}>
      {({ isExpanded }) => {
        return (
          <>
            <PaymentTypeAccordion.Trigger
              isExpanded={isExpanded}
              onExpand={onSelect}
            >
              <Flex direction="row" justifyContent="space-between" width="100%">
                <Text fontWeight={700}>
                  {!userHasSavedCards
                    ? t("Credit card")
                    : t("Use another card")}
                </Text>
                <Flex direction="row" gap="2xs">
                  <Icon.MastercardLogo />
                  <Icon.VisaLogo />
                  <Icon.AmexLogo />
                </Flex>
              </Flex>
            </PaymentTypeAccordion.Trigger>
            <PaymentTypeAccordion.Content>
              <NameElement
                form={manualCardEntryForm}
                onValidField={useCallback(() => {
                  setIsNameValid(true);
                }, [])}
                onInvalidField={useCallback(() => {
                  setIsNameValid(false);
                }, [])}
              />

              <CardElement
                onValidFields={useCallback(() => {
                  setIsCardInputValid(true);
                }, [])}
                onInvalidFields={useCallback(() => {
                  setIsCardInputValid(false);
                }, [])}
              />

              {supportsSavingCards && (
                <Box mt="md" mb="md">
                  {/* Use chakra checkbox so we get the tick animation */}
                  <Checkbox
                    isChecked={shouldSaveCard}
                    onChange={(e) => {
                      onToggleSaveCard(e.target.checked);
                    }}
                    sx={{
                      "& .chakra-checkbox__control": {
                        borderRadius: "6px",
                        "& .chakra-icon": {
                          width: "1.2rem",
                        },
                      },
                    }}
                  >
                    <Text fontSize="sm">
                      {t("Save your card to use for future payments")}
                    </Text>
                  </Checkbox>
                </Box>
              )}

              <PublicCheckoutBillingAddressForm
                form={addressForm}
                onValidFields={useCallback(() => {
                  setIsAddressValid(true);
                }, [])}
                onInvalidFields={useCallback(() => {
                  setIsAddressValid(false);
                }, [])}
                onBillingAddressChange={onBillingAddressChange}
              />
            </PaymentTypeAccordion.Content>
          </>
        );
      }}
    </PaymentTypeAccordion.Item>
  );
}

// Define a saved-card component handling a single payment method
type CardPaymentTypeSavedCardProps = BaseCardPaymentTypeProps & {
  paymentMethod: UserBasedUserPaymentMethodSerializer;
  showExpiration?: boolean;
  onSelect: (paymentMethod: UserBasedUserPaymentMethodSerializer) => void;
};

export function CardPaymentTypeSavedCard({
  onSelect,
  isDisabled = false,
  value,
  paymentMethod,
  showExpiration = false,
}: CardPaymentTypeSavedCardProps) {
  const cardBrand =
    paymentMethod.card_type && getCardBrandByName(paymentMethod.card_type);

  return (
    // Intentionally omit a fallback. This is because we want these to silent fail to a customer.
    // in contrast, manual card entry does not have a boundary and should propagate it's crashes up.
    <ErrorBoundary fallback={null}>
      <PaymentTypeAccordion.Item
        animation="slideDown 0.5s ease-out forwards"
        disabled={isDisabled}
        value={value}
      >
        {({ isExpanded }) => (
          <>
            <PaymentTypeAccordion.Trigger
              isExpanded={isExpanded}
              onExpand={() => onSelect(paymentMethod)}
            >
              <Flex direction="row" justifyContent="space-between" width="100%">
                <Flex direction="row" alignItems="center" gap="xs">
                  {paymentMethod.card_last4 && (
                    <Text
                      fontWeight={700}
                      title={`ID:${paymentMethod.id} | PG:${paymentMethod.payment_gateway_id}`}
                    >
                      {paymentMethod.card_last4}
                    </Text>
                  )}

                  {showExpiration &&
                    paymentMethod.exp_month != null &&
                    paymentMethod.exp_year != null && (
                      <Text fontSize="sm" color="gray.500">
                        {String(paymentMethod.exp_month).padStart(2, "0")}/
                        {String(paymentMethod.exp_year).slice(-2)}
                      </Text>
                    )}
                </Flex>
                {cardBrand && <CardBrandLogo brand={cardBrand.brand} />}
              </Flex>
            </PaymentTypeAccordion.Trigger>
          </>
        )}
      </PaymentTypeAccordion.Item>
    </ErrorBoundary>
  );
}
